/**
 * Place-name formatter for Find-2-Day.
 *
 * Combines two strategies:
 *   1. A lookup table of known Almería-province municipalities (plus major Spanish
 *      cities and common local areas) mapping a NORMALISED key -> canonical form
 *      with correct accents. This is the ONLY way accents are applied — we never
 *      "guess" an accent on an unknown place, which would risk creating wrong
 *      spellings (e.g. turning "Avila" into "Ávila" but also incorrectly
 *      touching foreign names like "Valparaiso").
 *   2. For anything not in the lookup, we Title-Case the input while keeping
 *      Spanish connectors (de, del, la, las, el, los, y, e, al, por, para, con)
 *      lowercase unless they are the first word, and preserving hyphens.
 *
 * Usage:
 *     import { formatPlaceName } from '@/utils/placeName';
 *     formatPlaceName('almeria')              -> 'Almería'
 *     formatPlaceName('san juan de los terreros') -> 'San Juan de los Terreros'
 *     formatPlaceName('huercal-overa')        -> 'Huércal-Overa'
 *     formatPlaceName('somePlaceWeDontKnow')  -> 'Someplacewedontknow' (safe title-case)
 */

// ---------- Lookup table ----------
// Keys MUST be:
//   - lowercased
//   - accent-stripped (use the `_normalise` fn at bottom for new additions)
//   - hyphens/whitespace preserved
// Values are the canonical Spanish/Catalan/Galician/Basque form with correct diacritics.

const CANONICAL_PLACES = {
  // Almería province — municipalities (all 103)
  'abla': 'Abla',
  'abrucena': 'Abrucena',
  'adra': 'Adra',
  'albanchez': 'Albánchez',
  'alboloduy': 'Alboloduy',
  'albox': 'Albox',
  'alcolea': 'Alcolea',
  'alcontar': 'Alcóntar',
  'alcudia de monteagud': 'Alcudia de Monteagud',
  'alhabia': 'Alhabia',
  'alhama de almeria': 'Alhama de Almería',
  'alicun': 'Alicún',
  'almeria': 'Almería',
  'almocita': 'Almócita',
  'alsodux': 'Alsodux',
  'antas': 'Antas',
  'arboleas': 'Arboleas',
  'armuna de almanzora': 'Armuña de Almanzora',
  'bacares': 'Bacares',
  'bayarcal': 'Bayárcal',
  'bayarque': 'Bayarque',
  'bedar': 'Bédar',
  'beires': 'Beires',
  'benahadux': 'Benahadux',
  'benitagla': 'Benitagla',
  'benizalon': 'Benizalón',
  'bentarique': 'Bentarique',
  'berja': 'Berja',
  'canjayar': 'Canjáyar',
  'cantoria': 'Cantoria',
  'carboneras': 'Carboneras',
  'castro de filabres': 'Castro de Filabres',
  'chercos': 'Chercos',
  'chirivel': 'Chirivel',
  'cobdar': 'Cóbdar',
  'cuevas del almanzora': 'Cuevas del Almanzora',
  'dalias': 'Dalías',
  'el ejido': 'El Ejido',
  'enix': 'Enix',
  'felix': 'Felix',
  'fines': 'Fines',
  'finana': 'Fiñana',
  'fondon': 'Fondón',
  'gador': 'Gádor',
  'garrucha': 'Garrucha',
  'gergal': 'Gérgal',
  'huecija': 'Huécija',
  'huercal de almeria': 'Huércal de Almería',
  'huercal-overa': 'Huércal-Overa',
  'illar': 'Íllar',
  'instincion': 'Instinción',
  'la mojonera': 'La Mojonera',
  'las tres villas': 'Las Tres Villas',
  'laroya': 'Laroya',
  'laujar de andarax': 'Laujar de Andarax',
  'lijar': 'Líjar',
  'los gallardos': 'Los Gallardos',
  'lubrin': 'Lubrín',
  'lucainena de las torres': 'Lucainena de las Torres',
  'lucar': 'Lúcar',
  'macael': 'Macael',
  'maria': 'María',
  'mojacar': 'Mojácar',
  'nacimiento': 'Nacimiento',
  'nijar': 'Níjar',
  'ohanes': 'Ohanes',
  'olula de castro': 'Olula de Castro',
  'olula del rio': 'Olula del Río',
  'oria': 'Oria',
  'padules': 'Padules',
  'partaloa': 'Partaloa',
  'paterna del rio': 'Paterna del Río',
  'pechina': 'Pechina',
  'pulpi': 'Pulpí',
  'purchena': 'Purchena',
  'ragol': 'Rágol',
  'rioja': 'Rioja',
  'roquetas de mar': 'Roquetas de Mar',
  'santa cruz de marchena': 'Santa Cruz de Marchena',
  'santa fe de mondujar': 'Santa Fe de Mondújar',
  'senes': 'Senés',
  'seron': 'Serón',
  'sierro': 'Sierro',
  'somontin': 'Somontín',
  'sorbas': 'Sorbas',
  'sufli': 'Suflí',
  'tabernas': 'Tabernas',
  'taberno': 'Taberno',
  'tahal': 'Tahal',
  'terque': 'Terque',
  'tijola': 'Tíjola',
  'turre': 'Turre',
  'turrillas': 'Turrillas',
  'uleila del campo': 'Uleila del Campo',
  'urracal': 'Urrácal',
  'velefique': 'Velefique',
  'velez-blanco': 'Vélez-Blanco',
  'velez-rubio': 'Vélez-Rubio',
  'vera': 'Vera',
  'viator': 'Viator',
  'vicar': 'Vícar',
  'zurgena': 'Zurgena',

  // Common local districts / areas / sub-places seen in the app
  'aguadulce': 'Aguadulce',
  'almerimar': 'Almerimar',
  'costa almeria': 'Costa Almería',
  'el toyo': 'El Toyo',
  'la envia': 'La Envía',
  'levante almeriense': 'Levante Almeriense',
  'mojacar playa': 'Mojácar Playa',
  'palomares': 'Palomares',
  'paseo de almeria': 'Paseo de Almería',
  'playa de los muertos': 'Playa de los Muertos',
  'retamar': 'Retamar',
  'san jose': 'San José',
  'san juan de los terreros': 'San Juan de los Terreros',
  'san miguel de cabo de gata': 'San Miguel de Cabo de Gata',
  'turaniana': 'Turaniana',
  'villaricos': 'Villaricos',

  // Region/province labels occasionally tagged
  'andalucia': 'Andalucía',
  'andalusia': 'Andalucía',

  // Major Spanish cities (for rare cross-country listings)
  'a coruna': 'A Coruña',
  'albacete': 'Albacete',
  'alicante': 'Alicante',
  'avila': 'Ávila',
  'badajoz': 'Badajoz',
  'barcelona': 'Barcelona',
  'bilbao': 'Bilbao',
  'burgos': 'Burgos',
  'caceres': 'Cáceres',
  'cadiz': 'Cádiz',
  'cartagena': 'Cartagena',
  'castellon': 'Castellón',
  'ceuta': 'Ceuta',
  'ciudad real': 'Ciudad Real',
  'cordoba': 'Córdoba',
  'cuenca': 'Cuenca',
  'donostia': 'Donostia',
  'gijon': 'Gijón',
  'girona': 'Girona',
  'granada': 'Granada',
  'guadalajara': 'Guadalajara',
  'huelva': 'Huelva',
  'huesca': 'Huesca',
  'jaen': 'Jaén',
  'la coruna': 'A Coruña',
  'las palmas': 'Las Palmas',
  'leon': 'León',
  'lerida': 'Lleida',
  'lleida': 'Lleida',
  'logrono': 'Logroño',
  'lugo': 'Lugo',
  'madrid': 'Madrid',
  'malaga': 'Málaga',
  'melilla': 'Melilla',
  'merida': 'Mérida',
  'murcia': 'Murcia',
  'orense': 'Ourense',
  'ourense': 'Ourense',
  'oviedo': 'Oviedo',
  'palencia': 'Palencia',
  'palma': 'Palma',
  'pamplona': 'Pamplona',
  'pontevedra': 'Pontevedra',
  'salamanca': 'Salamanca',
  'san sebastian': 'San Sebastián',
  'santa cruz de tenerife': 'Santa Cruz de Tenerife',
  'santander': 'Santander',
  'santiago de compostela': 'Santiago de Compostela',
  'segovia': 'Segovia',
  'sevilla': 'Sevilla',
  'soria': 'Soria',
  'tarragona': 'Tarragona',
  'teruel': 'Teruel',
  'toledo': 'Toledo',
  'valencia': 'València',
  'valladolid': 'Valladolid',
  'vitoria': 'Vitoria-Gasteiz',
  'vitoria-gasteiz': 'Vitoria-Gasteiz',
  'zamora': 'Zamora',
  'zaragoza': 'Zaragoza',
};

// Words that stay lowercase inside a multi-word name (unless first)
const LOWERCASE_CONNECTORS = new Set([
  'de', 'del', 'la', 'las', 'el', 'los', 'y', 'e', 'al', 'a',
  'por', 'para', 'con', 'da', 'das', 'do', 'dos', "l'", 'en',
]);

// Generic descriptors — kept lowercase mid-name (unless they form part of a
// canonical entry like "Mojácar Playa", in which case the lookup wins before
// this rule is even consulted).
const LOWERCASE_DESCRIPTORS = new Set([
  'centre', 'center', 'region', 'beach', 'area', 'zone',
  'district', 'old', 'town',
]);

// Normalise a string: lowercase + strip diacritics + collapse whitespace.
// Used only for lookup-key matching — the original input is otherwise preserved.
const _normalise = (s) => {
  if (!s) return '';
  return String(s)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
};

// Title-case a single word while preserving trailing punctuation.
// Handles hyphens: "huércal-overa" -> "Huércal-Overa"
const _titleCaseWord = (word, isFirst) => {
  if (!word) return word;
  // Hyphenated — recurse on parts but treat every segment as first-of-segment
  if (word.includes('-')) {
    return word
      .split('-')
      .map((part, idx) => _titleCaseWord(part, isFirst && idx === 0))
      .join('-');
  }
  const lower = word.toLowerCase();
  if (!isFirst && LOWERCASE_CONNECTORS.has(lower)) return lower;
  if (!isFirst && LOWERCASE_DESCRIPTORS.has(lower)) return lower;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/**
 * Format a place name for display.
 * Handles comma-separated compound strings like "Almeria, Andalusia" by formatting
 * each comma-separated segment independently ("Almería, Andalucía").
 * Never mutates stored data. Always safe — returns empty string for empty input.
 */
export const formatPlaceName = (raw) => {
  if (!raw || typeof raw !== 'string') return raw || '';
  const trimmed = raw.trim();
  if (!trimmed) return '';

  // Comma-separated compound: format each segment, rejoin with ", "
  if (trimmed.includes(',')) {
    return trimmed
      .split(',')
      .map((part) => formatPlaceName(part.trim()))
      .filter(Boolean)
      .join(', ');
  }

  const key = _normalise(trimmed);
  if (CANONICAL_PLACES[key]) return CANONICAL_PLACES[key];

  // Fallback: Title Case with Spanish connector rules — no fake accents.
  return trimmed
    .split(/\s+/)
    .map((w, idx) => _titleCaseWord(w, idx === 0))
    .join(' ');
};

// Test helper — also exported for unit-testing
export const _normaliseForTests = _normalise;
